import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAppVersionApi, updateAppVersionApi } from "../../../redux/api/api";

const VERSION_PATTERN = /^\d+(\.\d+){0,3}$/;

const DEFAULT_STORE_URL = {
  android: "https://play.google.com/store/apps/details?id=com.bharatonline.customer.app",
  ios: "",
};

const compareVersions = (a, b) => {
  const left = String(a || "0").split(".").map(Number);
  const right = String(b || "0").split(".").map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff) return diff > 0 ? 1 : -1;
  }
  return 0;
};

/**
 * Same rules as customer_app/src/utils/appUpdate.ts - keep the two in sync.
 * Returns "none" | "optional" | "required".
 */
const decideUpdate = (form, installed) => {
  if (!form.isActive || !VERSION_PATTERN.test(form.latestVersion) || !VERSION_PATTERN.test(installed)) return "none";
  if (compareVersions(installed, form.latestVersion) >= 0) return "none";
  if (form.minimumSupportedVersion && compareVersions(installed, form.minimumSupportedVersion) < 0) return "required";
  if (form.forceUpdate) return "required";
  return "optional";
};

const emptyForm = (platform) => ({
  latestVersion: "",
  minimumSupportedVersion: "",
  forceUpdate: false,
  title: "",
  message: "",
  releaseNotes: "",
  storeUrl: DEFAULT_STORE_URL[platform],
  isActive: false,
  updatedAt: null,
});

const fromDoc = (doc, platform) =>
  doc
    ? {
        latestVersion: doc.latestVersion || "",
        minimumSupportedVersion: doc.minimumSupportedVersion || "",
        forceUpdate: Boolean(doc.forceUpdate),
        title: doc.title || "",
        message: doc.message || "",
        releaseNotes: doc.releaseNotes || "",
        storeUrl: doc.storeUrl || DEFAULT_STORE_URL[platform],
        isActive: Boolean(doc.isActive),
        updatedAt: doc.updatedAt || null,
      }
    : emptyForm(platform);

const OUTCOME_COPY = {
  none: { label: "No prompt", color: "default", text: "Opens normally, nothing is shown." },
  optional: { label: "Optional update", color: "info", text: "Sees the update sheet once, can tap Later and keep using the app." },
  required: { label: "Update required", color: "error", text: "Blocked on a full-screen update page until they install the new version." },
};

/**
 * Per-platform store-update policy for the React Native customer app
 * (GET /api/app/version). One settings document per platform - no list.
 * Card/Switch/toast conventions mirror AppTheme/AppThemeForm.js.
 */
const AppVersionForm = () => {
  const [platform, setPlatform] = useState("android");
  const [forms, setForms] = useState({ android: emptyForm("android"), ios: emptyForm("ios") });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [probeVersion, setProbeVersion] = useState("");

  const form = forms[platform];
  const setField = (key, value) => setForms((prev) => ({ ...prev, [platform]: { ...prev[platform], [key]: value } }));

  useEffect(() => {
    getAppVersionApi()
      .then((res) => {
        const info = res?.data?.info || {};
        setForms({ android: fromDoc(info.android, "android"), ios: fromDoc(info.ios, "ios") });
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Couldn't load the version settings."))
      .finally(() => setLoading(false));
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!VERSION_PATTERN.test(form.latestVersion)) e.latestVersion = "Required, e.g. 2.1.0";
    if (form.minimumSupportedVersion) {
      if (!VERSION_PATTERN.test(form.minimumSupportedVersion)) e.minimumSupportedVersion = "Use a version like 2.0.0";
      else if (VERSION_PATTERN.test(form.latestVersion) && compareVersions(form.minimumSupportedVersion, form.latestVersion) > 0)
        e.minimumSupportedVersion = "Can't be higher than the latest version";
    }
    if (form.storeUrl && !/^(https?:\/\/|market:\/\/|itms-apps:\/\/)/i.test(form.storeUrl))
      e.storeUrl = "Must start with https://, market:// or itms-apps://";
    if (form.isActive && !form.storeUrl) e.storeUrl = "Required while published, or the Update button has nowhere to go";
    return e;
  }, [form]);

  const handleSave = async () => {
    if (Object.keys(errors).length) return toast.error(Object.values(errors)[0]);
    setSaving(true);
    try {
      const res = await updateAppVersionApi({ platform, ...form });
      setForms((prev) => ({ ...prev, [platform]: fromDoc(res?.data?.info, platform) }));
      toast.success(form.isActive ? `${platform === "ios" ? "iOS" : "Android"} update policy is live.` : "Saved (not live - switch on Publish to go live).");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading version settings…</Typography>
      </Box>
    );
  }

  // What does someone just below the latest version get? Drives the preview.
  const previewMode = form.forceUpdate ? "required" : "optional";
  const probe = probeVersion.trim();
  const probeOutcome = probe ? decideUpdate(form, probe) : null;
  const notes = form.releaseNotes
    .split("\n")
    .map((line) => line.replace(/^[\s\-*•]+/, "").trim())
    .filter(Boolean);

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>Customer App Version &amp; Updates</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>App Configuration</li>
          <li>App Version &amp; Updates</li>
        </ul>
      </div>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Card sx={{ flex: "1 1 620px" }}>
          <Tabs value={platform} onChange={(_, value) => setPlatform(value)} sx={{ px: 2, borderBottom: "1px solid #eee" }}>
            <Tab value="android" label={`Android${forms.android.isActive ? " · Live" : ""}`} />
            <Tab value="ios" label={`iOS${forms.ios.isActive ? " · Live" : ""}`} />
          </Tabs>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Alert severity="info" variant="outlined">
              Users <b>below the latest version</b> see an optional update they can dismiss. They are only{" "}
              <b>blocked</b> if they are below the minimum supported version, or if Force Update is on. Anyone already on
              the latest version never sees a prompt.
            </Alert>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Latest version"
                placeholder="e.g. 2.1.0"
                value={form.latestVersion}
                onChange={(e) => setField("latestVersion", e.target.value.trim())}
                error={Boolean(form.latestVersion) && Boolean(errors.latestVersion)}
                helperText={errors.latestVersion || "The version now live on the store"}
                sx={{ flex: "1 1 220px" }}
              />
              <TextField
                label="Minimum supported version (optional)"
                placeholder="Leave blank to never block"
                value={form.minimumSupportedVersion}
                onChange={(e) => setField("minimumSupportedVersion", e.target.value.trim())}
                error={Boolean(errors.minimumSupportedVersion)}
                helperText={errors.minimumSupportedVersion || "Older than this = must update"}
                sx={{ flex: "1 1 220px" }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={<Switch checked={form.forceUpdate} onChange={(e) => setField("forceUpdate", e.target.checked)} />}
                label="Force update (everyone below the latest version must update)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Use only for critical fixes. When off, the minimum supported version alone decides who is blocked.
              </Typography>
            </Box>

            <Divider />

            <TextField
              label="Title (optional)"
              placeholder="A New Version Is Available"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              label="Message (optional)"
              placeholder="We've improved your experience with performance enhancements, stability improvements, and new features."
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 240 }}
            />
            <TextField
              label="What's new (one item per line)"
              placeholder={"Faster ride booking\nLive driver tracking improvements\nBug fixes"}
              value={form.releaseNotes}
              onChange={(e) => setField("releaseNotes", e.target.value)}
              multiline
              minRows={3}
            />
            <TextField
              label={platform === "ios" ? "App Store URL" : "Play Store URL"}
              placeholder={platform === "ios" ? "https://apps.apple.com/app/id…" : DEFAULT_STORE_URL.android}
              value={form.storeUrl}
              onChange={(e) => setField("storeUrl", e.target.value.trim())}
              error={Boolean(errors.storeUrl)}
              helperText={errors.storeUrl || "Where the Update Now button takes the user"}
            />

            <Divider />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} />}
                label="Publish (live on the customer app)"
              />
              {form.updatedAt ? (
                <Chip size="small" variant="outlined" label={`Last updated ${new Date(form.updatedAt).toLocaleString()}`} />
              ) : (
                <Chip size="small" color="warning" variant="outlined" label="Never saved" />
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : `Save ${platform === "ios" ? "iOS" : "Android"} Settings`}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ flex: "0 1 320px", display: "flex", flexDirection: "column", gap: 3, position: "sticky", top: 16 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Check a version
              </Typography>
              <TextField
                size="small"
                fullWidth
                label="User's installed version"
                placeholder="e.g. 2.0.8"
                value={probeVersion}
                onChange={(e) => setProbeVersion(e.target.value)}
              />
              {probeOutcome ? (
                <Box sx={{ mt: 1.5 }}>
                  <Chip size="small" color={OUTCOME_COPY[probeOutcome].color} label={OUTCOME_COPY[probeOutcome].label} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {!form.isActive && probe ? "Not published, so " : ""}
                    {OUTCOME_COPY[probeOutcome].text}
                  </Typography>
                </Box>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Preview · {previewMode === "required" ? "Update required" : "Optional update"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Approximate - the app uses its own theme and layout.
              </Typography>
              <Box sx={{ borderRadius: 3, border: "1px solid #eee", overflow: "hidden", bgcolor: "#FCF9F0" }}>
                <Box sx={{ height: previewMode === "required" ? 40 : 90, bgcolor: previewMode === "required" ? "#FCF9F0" : "rgba(0,0,0,0.35)" }} />
                <Box sx={{ bgcolor: "#fff", borderRadius: "16px 16px 0 0", p: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    {form.title || (previewMode === "required" ? "Update Required" : "A New Version Is Available")}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#7A6A4E", mt: 0.5 }}>
                    {form.message ||
                      (previewMode === "required"
                        ? "This version is no longer supported. Please update to continue."
                        : "We've improved your experience with performance enhancements, stability improvements, and new features.")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                    <Chip size="small" variant="outlined" label="Current: 2.0.8" />
                    <Chip size="small" color="success" variant="outlined" label={`Latest: ${form.latestVersion || "—"}`} />
                  </Box>
                  {notes.length ? (
                    <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.5, fontSize: 12 }}>
                      {notes.slice(0, 4).map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </Box>
                  ) : null}
                  <Box sx={{ mt: 2, py: 1, textAlign: "center", borderRadius: 2, bgcolor: "#C1922E", color: "#fff", fontWeight: 700, fontSize: 13 }}>
                    Update Now
                  </Box>
                  {previewMode === "optional" ? (
                    <Box sx={{ mt: 1, py: 0.5, textAlign: "center", fontSize: 13, color: "#C1922E", fontWeight: 600 }}>Later</Box>
                  ) : null}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default AppVersionForm;
